import { CSSProperties, HTMLAttributes, RefObject } from 'react';

import { Image } from './Image';
import { PlayIcon } from './PlayIcon';
import styles from './SquareStory.module.css';
import { Video } from './Video';
import { STORY_LINE_CLAMP, STORY_SQUARE_RADIUS } from '../constants';
import { ImageFormat } from '../enums';
import {
  AdditionalProductFragment,
  FileFragment,
} from '../graphql/generated/graphql';
import { Story } from '../types';
import { StoryStyle } from './Story';
import { getAdditionalProducts } from '../utils';
import { AdditionalProduct } from './AdditionalProduct';

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  preload?: HTMLVideoElement['preload'];
  story: Story;
  video?: FileFragment;
  videoRef?: RefObject<HTMLVideoElement>;
  borderColor: CSSProperties['borderColor'];
  font: CSSProperties['font'];
  withBorder: boolean;
  withOverlay: boolean;
  withPlayIcon: boolean;
  withRadius: boolean;
  withShoppables: boolean;
  withTitle: boolean;
  style?: StoryStyle;
  onClick: (ap?: AdditionalProductFragment) => void;
}

export const SquareStory = ({
  preload,
  story,
  video,
  videoRef,
  borderColor,
  font,
  withBorder,
  withOverlay,
  withPlayIcon,
  withRadius,
  withShoppables,
  withTitle,
  style,
  onClick,
  ...divProps
}: Props) => {
  const additionalProducts = getAdditionalProducts(story);

  return (
    <div
      className={`${styles.square_wrapper} ${withBorder ? styles.square_border_wrapper : ''}`}
      style={
        {
          ...style,
          '--border-color': borderColor,
          borderRadius: withRadius
            ? style?.borderRadius || STORY_SQUARE_RADIUS
            : undefined,
        } as CSSProperties
      }
      onClick={() => onClick()}
      {...divProps}
    >
      {!video && (
        <Image
          className={styles.square_image}
          file={story.PreviewImage}
          alt={story.PreviewImage.alternativeText || ''}
          fallbackFormat={ImageFormat.SmallWebp}
          sizes='100vh'
        />
      )}
      {video && (
        <Video
          className={styles.square_video}
          ref={videoRef}
          file={video}
          preload={preload}
        />
      )}
      <div className={styles.square_positioner}>
        {withPlayIcon && (
          <PlayIcon variant='solid' className={styles.play_icon} />
        )}
        {withOverlay && (
          <div className={styles.bottom_container}>
            {withTitle && (
              <p
                className={`${styles.square_text} ${withShoppables && additionalProducts.length > 0 ? styles.square_text_with_shoppables : ''}`}
                style={{
                  font,
                  lineClamp: STORY_LINE_CLAMP,
                  WebkitLineClamp: STORY_LINE_CLAMP,
                }}
              >
                {story.Title}
              </p>
            )}
            {withShoppables && additionalProducts.length > 0 && (
              <div className={styles.additional_products}>
                {additionalProducts.map((ap) => (
                  <AdditionalProduct
                    key={ap.id}
                    additionalProduct={ap}
                    selectedVariant={ap.selectedVariant}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onClick(ap);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
